import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Truck,
  Mail,
  Phone,
  MapPin,
  Pencil,
  Trash2,
  Loader2,
  ReceiptText,
  Euro,
  UserRound,
  Globe2,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  tax_id: "",
  contact_person: "",
  email: "",
  phone: "",
  address: "",
  website: "",
  notes: "",
  active: true,
};

function eur(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="mantix-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">
          {value}
        </p>
        <p className="text-sm text-[#86868B]">{label}</p>
      </div>
    </div>
  );
}

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setSuppliers(data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const openCreate = () => {
    reset();
    setOpen(true);
  };

  const openEdit = (supplier) => {
    setEditing(supplier);
    setForm({
      name: supplier.name || "",
      tax_id: supplier.tax_id || "",
      contact_person: supplier.contact_person || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      address: supplier.address || "",
      website: supplier.website || "",
      notes: supplier.notes || "",
      active: supplier.active !== false,
    });
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error("Escribe el nombre del proveedor.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        tax_id: form.tax_id.trim(),
        contact_person: form.contact_person.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        website: form.website.trim(),
        notes: form.notes.trim(),
      };

      if (editing) {
        await api.put(`/suppliers/${editing.id}`, payload);
        toast.success("Proveedor actualizado.");
      } else {
        await api.post("/suppliers", payload);
        toast.success("Proveedor creado.");
      }

      setOpen(false);
      reset();
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (supplier) => {
    if (!window.confirm(`¿Eliminar o archivar "${supplier.name}"?`)) {
      return;
    }

    setDeletingId(supplier.id);
    try {
      const { data } = await api.delete(
        `/suppliers/${supplier.id}`
      );

      toast.success(
        data?.archived
          ? "Proveedor archivado porque tiene compras asociadas."
          : "Proveedor eliminado."
      );
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    if (!normalized) return suppliers;

    return suppliers.filter((supplier) =>
      [
        supplier.name,
        supplier.tax_id,
        supplier.contact_person,
        supplier.email,
        supplier.phone,
        supplier.address,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalized)
    );
  }, [suppliers, query]);

  const totalPurchased = suppliers.reduce(
    (total, supplier) =>
      total + Number(supplier.total_purchased || 0),
    0
  );

  const totalOrders = suppliers.reduce(
    (total, supplier) =>
      total + Number(supplier.purchases_count || 0),
    0
  );

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Proveedores"
        subtitle={`${suppliers.length} proveedores activos`}
        action={
          <Dialog
            open={open}
            onOpenChange={(value) => {
              setOpen(value);
              if (!value) reset();
            }}
          >
            <DialogTrigger asChild>
              <button
                type="button"
                onClick={openCreate}
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Nuevo proveedor
              </button>
            </DialogTrigger>

            <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-3xl border-none bg-white p-6">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {editing ? "Editar proveedor" : "Nuevo proveedor"}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={save} className="mt-5 space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm font-medium">
                      Nombre / Razón social
                    </label>
                    <input
                      value={form.name}
                      onChange={(event) =>
                        setForm({ ...form, name: event.target.value })
                      }
                      required
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      CIF / NIF
                    </label>
                    <input
                      value={form.tax_id}
                      onChange={(event) =>
                        setForm({ ...form, tax_id: event.target.value })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Persona de contacto
                    </label>
                    <input
                      value={form.contact_person}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          contact_person: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Email
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) =>
                        setForm({ ...form, email: event.target.value })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Teléfono
                    </label>
                    <input
                      value={form.phone}
                      onChange={(event) =>
                        setForm({ ...form, phone: event.target.value })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm font-medium">
                      Dirección
                    </label>
                    <input
                      value={form.address}
                      onChange={(event) =>
                        setForm({ ...form, address: event.target.value })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm font-medium">
                      Web
                    </label>
                    <input
                      value={form.website}
                      onChange={(event) =>
                        setForm({ ...form, website: event.target.value })
                      }
                      placeholder="https://..."
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Notas
                  </label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(event) =>
                      setForm({ ...form, notes: event.target.value })
                    }
                    className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 rounded-full border border-[#D2D2D7] py-3 font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white disabled:opacity-60"
                  >
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {saving
                      ? "Guardando..."
                      : editing
                        ? "Guardar cambios"
                        : "Crear proveedor"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          icon={Truck}
          label="Proveedores"
          value={suppliers.length}
          color="#0066CC"
        />
        <SummaryCard
          icon={ReceiptText}
          label="Pedidos históricos"
          value={totalOrders}
          color="#5856D6"
        />
        <SummaryCard
          icon={Euro}
          label="Comprado"
          value={eur(totalPurchased)}
          color="#34C759"
        />
      </section>

      <section className="mantix-card mb-6 p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar proveedor, CIF, contacto, email..."
            className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-4 text-sm outline-none"
          />
        </div>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-[#0066CC]" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No hay proveedores"
          subtitle="Crea tu primer proveedor para empezar a registrar compras."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {filtered.map((supplier) => (
            <article
              key={supplier.id}
              className="mantix-card p-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#EAF2FC] text-[#0066CC]">
                    <Truck className="h-5 w-5" />
                  </div>

                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-[#1D1D1F]">
                      {supplier.name}
                    </h3>
                    <p className="mt-0.5 text-xs text-[#86868B]">
                      {supplier.tax_id || "Sin CIF/NIF"}
                    </p>
                  </div>
                </div>

                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => openEdit(supplier)}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(supplier)}
                    disabled={deletingId === supplier.id}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30]"
                  >
                    {deletingId === supplier.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-2 text-sm text-[#86868B]">
                {supplier.contact_person && (
                  <p className="flex items-center gap-2">
                    <UserRound className="h-4 w-4" />
                    {supplier.contact_person}
                  </p>
                )}
                {supplier.email && (
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {supplier.email}
                  </p>
                )}
                {supplier.phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    {supplier.phone}
                  </p>
                )}
                {supplier.address && (
                  <p className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {supplier.address}
                  </p>
                )}
                {supplier.website && (
                  <p className="flex items-center gap-2">
                    <Globe2 className="h-4 w-4" />
                    {supplier.website}
                  </p>
                )}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 border-t border-[#E8E8ED] pt-4">
                <div className="rounded-xl bg-[#F5F5F7] p-3">
                  <p className="text-xs text-[#86868B]">Pedidos</p>
                  <p className="mt-1 font-mono font-semibold">
                    {supplier.purchases_count || 0}
                  </p>
                </div>
                <div className="rounded-xl bg-[#F5F5F7] p-3">
                  <p className="text-xs text-[#86868B]">
                    Total recibido
                  </p>
                  <p className="mt-1 font-mono font-semibold">
                    {eur(supplier.total_purchased)}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}